import { useEffect, useState } from "react";
import { dashFetch } from "@/api/client";
import { Card } from "@/components/Card";
import MfaPanel from "@/account/MfaPanel";
import type { AccountUser } from "@/types/account";

function apiError(e: unknown): string {
  const raw = e instanceof Error ? e.message : String(e);
  try {
    const j = JSON.parse(raw) as { error?: string; message?: string };
    const code = String(j.error || "");
    if (code === "exists") return "That email is already in use.";
    if (code === "invalid_email") return "Enter a valid email address.";
    if (code === "weak_password") return j.message || "Use at least 10 characters.";
    if (code === "need_current") return j.message || "Enter your current password.";
    if (code === "invalid_current") return j.message || "Current password is not correct.";
    return String(j.message || j.error || raw);
  } catch {
    return raw;
  }
}

function memberSince(ms?: number): string {
  const n = Number(ms || 0);
  if (!n) return "";
  try {
    return new Date(n).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

export default function ProfilePanel({
  user,
  onUpdated
}: {
  user: AccountUser;
  onUpdated: (next: AccountUser) => void;
}) {
  const [displayName, setDisplayName] = useState(String(user.displayName || ""));
  const [email, setEmail] = useState(String(user.email || ""));
  const [currentPassword, setCurrentPassword] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [profileMsg, setProfileMsg] = useState({ err: "", ok: "" });
  const [pwMsg, setPwMsg] = useState({ err: "", ok: "" });
  const [profileBusy, setProfileBusy] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);

  useEffect(() => {
    setDisplayName(String(user.displayName || ""));
    setEmail(String(user.email || ""));
  }, [user.displayName, user.email]);

  const saveProfile = async () => {
    setProfileBusy(true);
    setProfileMsg({ err: "", ok: "" });
    try {
      const body: { displayName: string; email?: string } = { displayName: displayName.trim() };
      if (email.trim() && email.trim().toLowerCase() !== String(user.email || "").toLowerCase()) {
        body.email = email.trim();
      }
      const r = await dashFetch<{ ok?: boolean; user?: AccountUser; error?: string; message?: string }>(
        "/dashboard/api/account/profile",
        { method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify(body) }
      );
      if (!r?.ok) throw new Error(JSON.stringify(r));
      if (r.user) onUpdated(r.user);
      setProfileMsg({ err: "", ok: "Profile saved." });
    } catch (e) {
      setProfileMsg({ err: apiError(e), ok: "" });
    } finally {
      setProfileBusy(false);
    }
  };

  const savePassword = async () => {
    setPwMsg({ err: "", ok: "" });
    if (password.length < 10) {
      setPwMsg({ err: "Use at least 10 characters.", ok: "" });
      return;
    }
    if (password !== confirm) {
      setPwMsg({ err: "New password and confirmation do not match.", ok: "" });
      return;
    }
    setPwBusy(true);
    try {
      const r = await dashFetch<{ ok?: boolean; user?: AccountUser; error?: string; message?: string }>(
        "/dashboard/api/account/profile",
        {
          method: "PATCH",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            password,
            currentPassword: user.hasPassword ? currentPassword : undefined
          })
        }
      );
      if (!r?.ok) throw new Error(JSON.stringify(r));
      if (r.user) onUpdated(r.user);
      setCurrentPassword("");
      setPassword("");
      setConfirm("");
      setPwMsg({ err: "", ok: user.hasPassword ? "Password updated." : "Password set. You can sign in with email and password." });
    } catch (e) {
      setPwMsg({ err: apiError(e), ok: "" });
    } finally {
      setPwBusy(false);
    }
  };

  const since = memberSince(user.createdAtMs);

  return (
    <div className="profileLayout">
      <Card title="Profile details">
        <p className="muted profileLead">
          This is how you appear across Sitewrap. Changing your email updates the address used for magic-link sign-in.
        </p>
        <div className="profileMeta">
          <span className="profileChip">{user.role || "user"}</span>
          {since ? <span className="muted">Member since {since}</span> : null}
        </div>
        <label className="profileField">
          <span>Display name</span>
          <input
            className="settingsInput"
            value={displayName}
            maxLength={80}
            placeholder="Your name"
            autoComplete="name"
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </label>
        <label className="profileField">
          <span>Email</span>
          <input
            className="settingsInput"
            type="email"
            value={email}
            placeholder="you@company.com"
            autoComplete="email"
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        {profileMsg.err ? <p className="err">{profileMsg.err}</p> : null}
        {profileMsg.ok ? <p className="ok">{profileMsg.ok}</p> : null}
        <div className="profileActions">
          <button type="button" className="btn primary" disabled={profileBusy} onClick={() => void saveProfile()}>
            {profileBusy ? "Saving…" : "Save profile"}
          </button>
        </div>
      </Card>

      <Card title="Password">
        <p className="muted profileLead">
          {user.hasPassword
            ? "Change the password used for dashboard sign-in. Magic-link email remains available."
            : "You currently sign in with a magic link. Set a password to also sign in with email and password."}
        </p>
        {user.hasPassword ? (
          <label className="profileField">
            <span>Current password</span>
            <input
              className="settingsInput"
              type="password"
              value={currentPassword}
              autoComplete="current-password"
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </label>
        ) : null}
        <label className="profileField">
          <span>New password</span>
          <input
            className="settingsInput"
            type="password"
            value={password}
            autoComplete="new-password"
            placeholder="At least 10 characters"
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
        <label className="profileField">
          <span>Confirm new password</span>
          <input
            className="settingsInput"
            type="password"
            value={confirm}
            autoComplete="new-password"
            onChange={(e) => setConfirm(e.target.value)}
          />
        </label>
        {pwMsg.err ? <p className="err">{pwMsg.err}</p> : null}
        {pwMsg.ok ? <p className="ok">{pwMsg.ok}</p> : null}
        <div className="profileActions">
          <button type="button" className="btn primary" disabled={pwBusy} onClick={() => void savePassword()}>
            {pwBusy ? "Saving…" : user.hasPassword ? "Update password" : "Set password"}
          </button>
        </div>
      </Card>

      <div className="profileMfaWide">
        <Card title="Authenticator MFA">
          <MfaPanel />
        </Card>
      </div>
    </div>
  );
}
